import { useCallback, useEffect, useState } from 'react';
import { eventosService } from '../../services/api';
import Dialogo from '../../components/ui/Dialogo';
import { btnPrimario, btnSecundario, chip, segmentos, segmento } from '../../components/ui/estilos';
import { Campo, Contador, dosColumnas, entrada, tarjetaLista } from './campos';

const ESTADOS: Record<string, { label: string; bg: string; fg: string }> = {
  pendiente:  { label: 'Pendiente',  bg: 'var(--well)', fg: 'var(--muted)' },
  confirmado: { label: 'Confirmado', bg: 'var(--ok-bg)', fg: 'var(--ok)' },
  completado: { label: 'Completado', bg: 'var(--info-bg)', fg: 'var(--info)' },
  cancelado:  { label: 'Cancelado',  bg: 'var(--danger-bg)', fg: 'var(--danger)' },
};
// Rango de la lista: lo reciente y lo que viene.
const DIAS_ATRAS = 30, DIAS_ADELANTE = 365;
const iso = (d: Date) => d.toLocaleDateString('en-CA');
const masDias = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return iso(d); };
const fechaCorta = (f: string) => new Date(`${String(f).slice(0, 10)}T12:00:00`).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });

interface Form { nombre: string; responsable: string; telefono: string; fecha: string; hora_inicio: string; hora_fin: string; personas: number; notas: string; estado: string; mesa_ids: string[] }
const deEvento = (e: any): Form => ({
  nombre: e.nombre, responsable: e.responsable, telefono: e.telefono || '', fecha: String(e.fecha).slice(0, 10),
  hora_inicio: String(e.hora_inicio || '').slice(0, 5), hora_fin: String(e.hora_fin || '').slice(0, 5), personas: e.personas,
  notas: e.notas || '', estado: e.estado, mesa_ids: (e.mesas || []).map((m: any) => m.mesa_id),
});

export default function Eventos({ mesas, say }: { mesas: any[]; say: (m: string) => void }) {
  const [lista, setLista] = useState<any[]>([]);
  const [busca, setBusca] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [nuevo, setNuevo] = useState(false);

  const cargar = useCallback(() => eventosService.getAll(masDias(-DIAS_ATRAS), masDias(DIAS_ADELANTE))
    .then(r => setLista(r.data)).catch(() => say('No se pudieron cargar los eventos.')), [say]);
  useEffect(() => { cargar(); }, [cargar]);

  const sel = lista.find(e => e.id === selId) || null;
  useEffect(() => { setForm(sel ? deEvento(sel) : null); }, [selId, lista]); // eslint-disable-line react-hooks/exhaustive-deps
  const cambio = !!(sel && form && JSON.stringify(form) !== JSON.stringify(deEvento(sel)));
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm(f => f && { ...f, [k]: v });

  const guardar = async (f: Form = form!) => {
    if (!sel) return;
    if (!f.nombre.trim() || !f.responsable.trim() || !f.fecha || !f.hora_inicio) { say('Nombre, responsable, fecha y hora de inicio son obligatorios.'); return; }
    try {
      await eventosService.actualizar(sel.id, { ...f, nombre: f.nombre.trim(), responsable: f.responsable.trim(), hora_fin: f.hora_fin || null });
      say('Evento guardado.'); cargar();
    } catch (e: any) { say(e.response?.data?.error || 'No se pudo guardar el evento.'); }
  };

  const eliminar = async () => {
    if (!sel || !window.confirm(`¿Eliminar "${sel.nombre}"? No se puede deshacer.`)) return;
    try { await eventosService.eliminar(sel.id); say(`${sel.nombre} eliminado.`); setSelId(null); cargar(); }
    catch (e: any) { say(e.response?.data?.error || 'No se pudo eliminar.'); }
  };

  const q = busca.trim().toLowerCase();
  const visibles = lista.filter(e => !q || [e.nombre, e.responsable].some((s: string) => (s || '').toLowerCase().includes(q)));

  return (
    <div className="paneles" style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'clamp(280px,34%,400px) minmax(320px,1fr)' }}>
      <aside aria-label="Eventos" style={{ borderRight: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: 16, flex: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={() => setNuevo(true)} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>+ Nuevo evento</button>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar evento…" aria-label="Buscar evento" style={{ ...entrada, height: 46 }} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!visibles.length && <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--muted)', fontSize: 15 }}>No hay eventos con ese filtro.</div>}
          {visibles.map(e => {
            const st = ESTADOS[e.estado] || ESTADOS.pendiente;
            return (
              <button key={e.id} onClick={() => setSelId(e.id)} aria-pressed={selId === e.id} style={tarjetaLista(selId === e.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 700 }}>{e.nombre}</span>
                  <span style={{ ...chip(st.bg, st.fg), fontSize: 12, padding: '2px 8px' }}>{st.label}</span>
                </div>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>{fechaCorta(e.fecha)} · {e.personas} personas{e.mesas?.length ? ` · ${e.mesas.length} mesas` : ''}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <main style={{ minHeight: 0, overflowY: 'auto', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!sel || !form ? (
          <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 16 }}>Elige un evento para ver su detalle.</div>
        ) : <>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <input value={form.nombre} onChange={e => set('nombre', e.target.value)} aria-label="Nombre del evento" maxLength={200}
              style={{ fontSize: 24, fontWeight: 700, border: 'none', background: 'transparent', color: 'var(--ink)', padding: '2px 4px', borderRadius: 6, flex: 1, minWidth: 0 }} />
            <span style={{ ...chip(ESTADOS[sel.estado].bg, ESTADOS[sel.estado].fg), padding: '6px 14px' }}>{ESTADOS[sel.estado].label}</span>
          </div>
          <div style={dosColumnas}>
            <Campo label="Responsable"><input value={form.responsable} onChange={e => set('responsable', e.target.value)} maxLength={200} style={entrada} /></Campo>
            <Campo label="Teléfono"><input type="tel" value={form.telefono} onChange={e => set('telefono', e.target.value)} maxLength={30} style={entrada} /></Campo>
          </div>
          <div style={dosColumnas}>
            <Campo label="Fecha"><input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)} style={entrada} /></Campo>
            <div style={dosColumnas}>
              <Campo label="Inicio"><input type="time" value={form.hora_inicio} onChange={e => set('hora_inicio', e.target.value)} style={entrada} /></Campo>
              <Campo label="Fin"><input type="time" value={form.hora_fin} onChange={e => set('hora_fin', e.target.value)} style={entrada} /></Campo>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 260 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>Personas</span>
            <Contador label="Personas" valor={form.personas} onCambio={n => set('personas', n)} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>Mesas asignadas · {form.mesa_ids.length}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {mesas.map(m => {
                const on = form.mesa_ids.includes(m.id);
                return (
                  <button key={m.id} type="button" aria-pressed={on} onClick={() => set('mesa_ids', on ? form.mesa_ids.filter(x => x !== m.id) : [...form.mesa_ids, m.id])}
                    style={{ minWidth: 64, height: 48, padding: '0 12px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
                      background: on ? 'var(--strong)' : 'var(--surface)', color: on ? 'var(--on-strong)' : 'var(--ink)', border: on ? 'none' : '1px solid var(--line-strong)' }}>
                    {m.numero}<span style={{ fontWeight: 400, fontSize: 13 }}> · {m.capacidad}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div role="radiogroup" aria-label="Estado" style={{ ...segmentos, alignSelf: 'flex-start', flexWrap: 'wrap' }}>
            {Object.entries(ESTADOS).map(([k, v]) => (
              <button key={k} role="radio" aria-checked={form.estado === k} onClick={() => set('estado', k)} style={{ ...segmento(form.estado === k), height: 44, fontSize: 15 }}>{v.label}</button>
            ))}
          </div>
          <Campo label="Notas">
            <textarea value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Ej. montaje especial, menú personalizado…"
              style={{ ...entrada, height: 'auto', minHeight: 80, padding: '12px 14px', resize: 'vertical' }} />
          </Campo>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {cambio && <button onClick={() => guardar()} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>Guardar cambios</button>}
            <button onClick={eliminar} style={{ background: 'none', border: 'none', fontSize: 14, fontWeight: 700, color: 'var(--danger)', cursor: 'pointer', padding: '0 4px', height: 40 }}>Eliminar evento</button>
          </div>
        </>}
      </main>

      {nuevo && <NuevoEvento say={say} onCerrar={() => setNuevo(false)} onCreado={e => { setNuevo(false); setSelId(e.id); cargar(); }} />}
    </div>
  );
}

function NuevoEvento({ say, onCerrar, onCreado }: { say: (m: string) => void; onCerrar: () => void; onCreado: (e: any) => void }) {
  const [f, setF] = useState({ nombre: '', responsable: '', telefono: '', fecha: '', hora_inicio: '', personas: 20 });
  const [error, setError] = useState('');
  const crear = async () => {
    if (!f.nombre.trim() || !f.responsable.trim() || !f.fecha || !f.hora_inicio) { setError('Captura nombre, responsable, fecha y hora.'); return; }
    try {
      const { data } = await eventosService.crear({ ...f, nombre: f.nombre.trim(), responsable: f.responsable.trim(), estado: 'pendiente', mesa_ids: [] });
      say(`${f.nombre.trim()} creado.`);
      onCreado(data);
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo crear el evento.'); }
  };
  return (
    <Dialogo titulo="Nuevo evento" onCerrar={onCerrar} ancho={460}>
      <input autoFocus placeholder="Nombre del evento" value={f.nombre} onChange={e => setF(x => ({ ...x, nombre: e.target.value }))} maxLength={200} style={entrada} aria-label="Nombre del evento" />
      <div style={dosColumnas}>
        <input placeholder="Responsable" value={f.responsable} onChange={e => setF(x => ({ ...x, responsable: e.target.value }))} maxLength={200} style={entrada} aria-label="Responsable" />
        <input type="tel" placeholder="Teléfono" value={f.telefono} onChange={e => setF(x => ({ ...x, telefono: e.target.value }))} maxLength={30} style={entrada} aria-label="Teléfono" />
      </div>
      <div style={dosColumnas}>
        <input type="date" value={f.fecha} onChange={e => setF(x => ({ ...x, fecha: e.target.value }))} style={entrada} aria-label="Fecha" />
        <input type="time" value={f.hora_inicio} onChange={e => setF(x => ({ ...x, hora_inicio: e.target.value }))} style={entrada} aria-label="Hora de inicio" />
      </div>
      <Contador label="Personas" valor={f.personas} onCambio={n => setF(x => ({ ...x, personas: n }))} />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCerrar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
        <button onClick={crear} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16 }}>Crear evento</button>
      </div>
    </Dialogo>
  );
}
